import { siteConfig } from '@sighthost/config';

/**
 * partnerLinkMessage — paste-ready campaign messages built around the partner
 * acquisition link.
 *
 * Mirrors shared/lib/payLinkMessage.ts, and deliberately does NOT reuse it: that
 * builder is a payment request (reference, exact amount, "this link stops working
 * once paid"). None of those are true here. This is an introduction, not an
 * invoice, and borrowing payment framing would make a first-contact message read
 * like a demand for money from an unknown number.
 *
 * ── CT-10: the customer never learns a partner was involved ────────────────
 * SightHost does not white-label. Nothing here names the partner company, and
 * the link is an opaque token, so a prospect who reads the message or hovers the
 * URL sees only SightHost. The person pasting it knows; the recipient does not.
 * That is the requirement, not an oversight.
 *
 * ── CT-1: one link, no per-agent variants ─────────────────────────────────
 * Every agent at a company copies the SAME link. There is no agent identifier to
 * thread through and none should be added: per-agent links were considered and
 * rejected commercially, and adding one here would silently create the variants
 * the agreement excludes.
 *
 * PLAIN ASCII ONLY. No em-dashes, no smart quotes. WhatsApp and SMS gateways
 * mangle some Unicode punctuation, and the habit is house style across all
 * generated customer-facing copy (see payLinkMessage.ts for the origin).
 */

export interface PartnerLinkMessageInput {
  /** Absolute campaign URL, e.g. https://www.sighthost.online/p/<token>. */
  linkUrl: string;
  /** Sender's display name. Omitted from the sign-off when absent. */
  senderName?: string | null;
}

/** The campaign link for a token. One place builds this URL. */
export function buildPartnerLinkUrl(token: string): string {
  return `${siteConfig.url.replace(/\/$/, '')}/p/${token}`;
}

function signOff(senderName?: string | null): string {
  return senderName ? `${senderName}\nSightHost` : 'SightHost';
}

/**
 * WhatsApp: short enough to read without tapping "read more", which is roughly
 * where WhatsApp truncates. The link goes LAST so it is the final thing on
 * screen and the obvious next action.
 */
export function buildWhatsAppMessage(input: PartnerLinkMessageInput): string {
  return [
    'Good day,',
    '',
    'Does your business still run on a Gmail or Outlook address?',
    '',
    'A professional email on your own domain (you@yourbusiness.co.za) tells',
    'customers you are established. SightHost sets it up for you, with hosting',
    'for your website, from R79 per month. Your .co.za domain is free for the',
    'first year.',
    '',
    'Have a look at the packages and get a quote here:',
    input.linkUrl,
    '',
    signOff(input.senderName),
  ].join('\n');
}

/** Email: subject line first, then body, so the whole thing pastes in one go. */
export function buildEmailBlock(input: PartnerLinkMessageInput): string {
  return [
    'Subject: A professional email address for your business',
    '',
    'Good day,',
    '',
    'Most small businesses market themselves well on Facebook and WhatsApp, but',
    'still send quotes from a Gmail or Outlook address. It is the one detail that',
    'makes an established business look like a side project.',
    '',
    'SightHost gives you email on your own domain (you@yourbusiness.co.za),',
    'website hosting, and support from a South African team. Packages start at',
    'R79 per month and your .co.za domain is free for the first year.',
    '',
    'You can see the packages and request a quote here:',
    input.linkUrl,
    '',
    'No obligation, and the quote is valid for 14 days.',
    '',
    'Kind regards,',
    signOff(input.senderName),
    siteConfig.supportEmail,
  ].join('\n');
}
